/**
 * <TutorInvoiceRow> — une facture du masteur, sur une ligne de « Mes factures » (molecule).
 *
 * Ce que montrait la ligne du tableau d'aujourd'hui (`TutoringInvoicesScreen`) : la date, qui
 * l'émet et à qui, le montant, puis ses deux liens. « Facture » ouvre le PDF ; quand il n'existe
 * pas encore, le même lien le fait générer, et la ligne attend (`generating`) le temps qu'il
 * arrive. « Récapitulatif » n'existe que si le récapitulatif des paiements existe.
 *
 * Elle ne dessine pas de carte : les factures sont UNE carte-liste.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { CardListRow } from "../CardList"
import { type UITutorInvoice } from "../../../ports/primary/ui/types/tutorCompte"

export type TutorInvoiceRowProps = {
    invoice: UITutorInvoice
    /** « Facture » : ouvre le PDF, ou le fait générer. */
    onOpenInvoice: (invoice: UITutorInvoice) => void
    onOpenPaymentRecap: (invoice: UITutorInvoice) => void
    /** Le PDF de cette facture est en cours de génération. */
    generating?: boolean
    /** Le filet du dessus. */
    divided?: boolean
    testID?: string
}

const TEXTS = {
    parties: (issuer: string, recipient: string) => `De : ${issuer} · À : ${recipient}`,
    invoice: "Facture",
    generating: "Génération...",
    recap: "Récapitulatif",
}

export function TutorInvoiceRow(props: TutorInvoiceRowProps) {
    const { invoice, onOpenInvoice, onOpenPaymentRecap, generating = false, divided = false, testID } = props

    return (
        <CardListRow divided={divided} testID={testID}>
            <Stack gap="2">
                <Row gap="3" justify="between" align="start">
                    <Stack gap="1" style={styles.body}>
                        <Text weight="bold" color="primary">
                            {invoice.dateLabel}
                        </Text>
                        <Text size="text-sm" color="text-muted">
                            {TEXTS.parties(invoice.issuer, invoice.recipient)}
                        </Text>
                    </Stack>
                    <Text size="text-lg" weight="bold">
                        {invoice.amountLabel}
                    </Text>
                </Row>
                <Row gap="4">
                    <TextButton
                        label={generating ? TEXTS.generating : TEXTS.invoice}
                        size="sm"
                        disabled={generating}
                        onPress={() => onOpenInvoice(invoice)}
                    />
                    {invoice.hasPaymentRecap && (
                        <TextButton label={TEXTS.recap} size="sm" onPress={() => onOpenPaymentRecap(invoice)} />
                    )}
                </Row>
            </Stack>
        </CardListRow>
    )
}

const styles = StyleSheet.create(() => ({
    body: { flexShrink: 1 },
}))
